import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, FileText, AlertCircle, Phone, Video, UserX } from 'lucide-react';

const Disclaimer = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const terms = [
    {
      icon: Video,
      text: t.disclaimerTerm1
    },
    {
      icon: Phone,
      text: t.disclaimerTerm2
    },
    {
      icon: UserX,
      text: t.disclaimerTerm3
    },
    {
      icon: Shield,
      text: t.disclaimerTerm4
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t.disclaimerTitle}
          </h1>
          <p className="text-gray-600">
            {t.disclaimerSubtitle}
          </p>
        </div>

        {/* Main Content Card */}
        <Card className="shadow-lg">
          <CardHeader className="bg-blue-50 border-b">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Shield className="w-5 h-5" />
              {t.disclaimerGoal}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Introduction */}
            <p className="text-gray-700 mb-6 leading-relaxed">
              {t.disclaimerIntro}
            </p>

            {/* Terms List */}
            <div className="space-y-4">
              {terms.map((term, index) => {
                const Icon = term.icon;
                return (
                  <div 
                    key={index} 
                    className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-bold text-sm">{index + 1}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-700 leading-relaxed">
                        {term.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Important Notice */}
            <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-800 mb-1">
                    {t.importantNotice}
                  </h3>
                  <p className="text-yellow-700 text-sm leading-relaxed">
                    {t.disclaimerNotice}
                  </p>
                </div>
              </div>
            </div>

            {/* Last Modified */}
            <div className="mt-8 pt-4 border-t text-center">
              <p className="text-sm text-gray-500">
                {t.lastModified}: {new Date().toLocaleDateString(language === 'en' ? 'en-CA' : 'fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            {t.disclaimerContact}{' '}
            <a 
              href="mailto:MB.RCMP.CAPTURE@rcmp-grc.gc.ca" 
              className="text-blue-600 hover:underline"
            >
              MB.RCMP.CAPTURE@rcmp-grc.gc.ca
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;
